<template>
  <asc-spinner-wrapper :loading="loading">
    <div
      v-if="!isEmpty"
      v-el-resize
      class="asc-stacked-chart--container"
      @resize="handleResize"
    >
      <asc-chart
        :options="getChartOptions"
        @onSeriesClick="onSeriesClick"
      ></asc-chart>
    </div>
    <asc-no-data v-if="isEmpty"></asc-no-data>
  </asc-spinner-wrapper>
</template>

<script>
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import AscNoData from '@/components/atoms/AscNoData'
import AscChart from './AscChart'
import _ from 'lodash'
import variables from '@/styles/_varsJs.scss'

export default {
  name: 'AscStackedChart',

  components: {
    AscChart,
    AscSpinnerWrapper,
    AscNoData,
  },

  props: {
    values: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
    seriesName: {
      type: String,
      default: '',
    },
    categoryName: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    onClick: {
      type: Function,
      default: () => {},
    },
    options: {
      type: Object,
      default() {
        return {}
      },
    },
  },

  data() {
    const _that = this
    return {
      defaultOptions: {
        chart: {
          height: 272,
          spacing: [16, 24, 16, 16],
          style: {
            fontFamily: variables.chartfontfamily,
          },
        },
        title: {
          text: null,
        },
        subtitle: {
          align: 'left',
          style: {
            color: variables.gray1000,
            fontSize: variables.fontsizemd,
          },
        },
        xAxis: {
          labels: {
            style: {
              color: variables.primaryblack,
              fontSize: variables.fontsizesm,
            },
          },
          title: {
            text: null,
          },
        },
        yAxis: {
          min: 0,
          gridLineDashStyle: variables.chartgridlinestyle,
          gridLineColor: variables.gray400,
          labels: {
            style: {
              color: variables.primaryblack,
              fontSize: variables.fontsizesm,
            },
          },
          title: {
            text: null,
          },
        },
        tooltip: {
          backgroundColor: variables.primaryblack,
          style: {
            color: variables.primarywhite,
            fontSize: variables.fontsizesm,
          },
          borderWidth: 0,
        },
        plotOptions: {
          bar: {
            dataLabels: {
              enabled: true,
            },
          },
          series: {
            color: variables.accent400,
            cursor: 'pointer',
            events: {
              click: function (event) {
                _that.$emit('onSeriesClick', event)
              },
            },
            states: {
              hover: {
                color: variables.accent300,
              },
            },
          },
        },
        legend: {
          reversed: true,
        },
        series: [],
      },
      chartWidth: 0,
    }
  },

  computed: {
    isEmpty() {
      return this.values.length === 0
    },
    getChartOptions() {
      const _that = this
      if (this.values.length) {
        const chart = this.buildChart
        const subtitle = this.buildSubtitle
        const series = this.buildSeries
        const xAxisOptions = this.buildXAxisOptions
        const tooltipOptions = this.buildTooltipOptions
        const options = _.merge(
          {
            ...this.defaultOptions,
            ...chart,
            ...subtitle,
            ...series,
            ...xAxisOptions,
            ...tooltipOptions,
          },
          _that.options,
        )
        return options
      }
      return this.defaultOptions
    },
    buildChart() {
      return {
        chart: {
          width: this.chartWidth !== null ? this.chartWidth : null,
          height: 312,
          spacing: [16, 24, 16, 16],
          style: {
            fontFamily: variables.chartfontfamily,
          },
        },
      }
    },
    buildSubtitle() {
      return {
        subtitle: {
          align: 'left',
          style: {
            color: variables.gray1000,
            fontSize: variables.fontsizemd,
            fontFamily: 'Roboto',
            textTransform: 'uppercase',
            fontWeight: '500',
            letterSpacing: '1px',
            lineHeight: '18px',
          },
          text: this.title,
          widthAdjust: 30,
        },
      }
    },
    buildXAxisOptions() {
      return {
        xAxis: {
          categories: this.getCategories(),
          labels: {
            style: {
              color: variables.primaryblack,
              fontSize: variables.fontsizesm,
            },
          },
        },
      }
    },
    buildSeries() {
      return {
        series: [
          {
            type: 'bar',
            name: this.seriesName,
            data: this.getSeriesData(),
            showInLegend: false,
          },
        ],
      }
    },
    buildTooltipOptions() {
      let tooltipOptions = {}
      if (this.values.length) {
        tooltipOptions = {
          tooltip: {
            formatter: function () {
              return `${this.x}: ${this.y}`
            },
            backgroundColor: variables.primaryblack,
            style: {
              color: variables.primarywhite,
              fontSize: variables.fontsizesm,
            },
            borderWidth: 0,
          },
        }
      }
      return tooltipOptions
    },
  },

  methods: {
    getCategories() {
      if (this.values.length > 0) {
        const categories = this.values.map(item => item.text)
        return categories
      }
      return []
    },
    getSeriesData() {
      if (this.values.length > 0) {
        const data = this.values.map(item => item.count)
        return data
      }
      return {
        series: [],
      }
    },
    onSeriesClick(event) {
      this.onClick(event.point.category)
    },
    handleResize(e) {
      this.chartWidth = e.detail.width
    },
  },
}
</script>
